import dayjs, { Dayjs } from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import orderBy from 'lodash/orderBy';
import partition from 'lodash/partition';
import { ITipsBreakdown, ITipsBreakdownLineItem } from 'screens/MakePayment/Calculator/types';
import {
  CalculatorParticipantType,
  ChangeLogAction,
  ChangeLogEntity,
  ChangeLogParticipantType,
  CheckModifiedDetails,
  Days,
  DEFAULT_POOL_ID,
  DEFAULT_POOL_NAME,
  DEFAULT_POOL_UNKNOWN_JOB_TITLE,
  DisbursementMethod,
  ICalculatorParticipant,
  ICalculatorPaymentSegment,
  ICalculatorPaymentSet,
  ICalculatorRule,
  ICalculatorRules,
  ICategoryGroup,
  ICheck,
  IDaySegment,
  IHoldbackFixed,
  IHoldbackPercent,
  IPaymentSetCalculations,
  IPOSProviderSchedule,
  IPOSResults,
  IPOSSegmentTotals,
  IPOSTicket,
  IPOSTicketCategory,
  IPOSTicketSplitOverrideShare,
  IPosUnclosedChecksError,
  ISplitParticipant,
  ITipCalculatorChangeLogEntry,
  ITipCalculatorEmployeeTicket,
  ITipCalculatorParticipant,
  ITipCalculatorPayee,
  ITipCalculatorRequest,
  ITipCalculatorRequestEmployee,
  ITipCalculatorRequestEmployeeSchedule,
  ITipCalculatorResult,
  ITipCalculatorResultEmployee,
  ITipCalculatorResultIgnoredCheck,
  ITipCalculatorResultParticipant,
  ITipCalculatorResultSplitChecks,
  ITipCalculatorResultWarning,
  LaborDataDetails,
  RuleFundSource,
  ShareRuleType,
  SourcePaymentType,
  SourceType,
  TicketOverrideType,
  TipCalculatorMethod,
  TipEditedDetails,
} from 'types';
import { calculateHoldbackValue, centsToDollarsDisplay, Duration, sortAlphaNumerically, sumBy, toTitleCase } from 'utils';

dayjs.extend(isSameOrBefore);

interface IPOSResultSegment {
  id: string;
  name: string;
  start?: string;
  end?: string;
  endsNextDay?: boolean;
}

interface ITicketTotals {
  autoGratuity: {
    gross: number;
    net: number;
    cash: number;
    creditCard: number;
    creditCardFee: number;
  };
  tip: {
    gross: number;
    net: number;
    cash: number;
    creditCard: number;
    creditCardFee: number;
  };
  gross: number;
  net: number;
}

export class TipCalculationNoDataError extends Error {}

export enum TipCalculationValidationType {
  NO_DATA = 'no_data',
  NO_RULE_FOR_JOB = 'no_rule_for_job',
  NO_TIP_SHARE_RULE_FOR_JOB = 'no_tip_share_rule_for_job',
  ZERO_POINT_RULE_FOR_JOB = 'zero_point_rule_for_job',
  EMPLOYEES_INVALID_TIME_CLOCK_ENTRIES = 'employees_invalid_time_clock_entries',
  EMPLOYEES_STILL_CLOCKED_IN = 'employees_still_clocked_in',
  EMPLOYEES_NO_BREAK_END = 'employees_no_break_end',
  NO_ELIGIBLE_EMPLOYEES = 'no_eligible_employees',
  NO_LABOR_FOR_TICKETS = 'no_labor_for_tickets',
}

interface ITipCalcValidationError {
  type: TipCalculationValidationType;
  message: string;
  jobs?: string[] | { title: string; total: number; employees: { name: string; total: number }[] }[];
  employees?: { name: string; jobTitle: string; start: string; end: string; totalMinWorked: number }[];
}

export class PosUnclosedChecksError extends Error {
  public errors: IPosUnclosedChecksError[];

  constructor(errors: IPosUnclosedChecksError[]) {
    super('Unclosed checks.');
    this.errors = errors;
  }
}

export class TipCalculationValidationError extends Error {
  public errors: ITipCalcValidationError[];

  constructor(message: string, errors: ITipCalcValidationError[]) {
    super(message);
    this.errors = errors;
  }
}

interface INoLaborForTicketsError {
  code?: string;
  msg: string;
  allowSkip?: boolean;
  tickets?: ITipCalculatorEmployeeTicket[];
}

export class TipCalculationNoLaborForTicketsError extends Error {
  public errors: INoLaborForTicketsError[];

  constructor(message: string, errors: INoLaborForTicketsError[]) {
    super(message);
    this.errors = errors;
  }
}

// Build the employees array for the tip calculator request.
export const buildTipCalculatorEmployees = (integrationData: IPOSResults, calcRules: ICalculatorRule[], segment: IPOSResultSegment): ITipCalculatorRequestEmployee[] => {
  const results: ITipCalculatorRequestEmployee[] = [];

  // Build the tip calculator employee object for each segment
  integrationData.data.forEach((data) => {
    data.segments
      .filter((dataSegment) => dataSegment.id === segment.id)
      .forEach((segmentData) => {
        if (
          calcRules.find(
            (rule) =>
              (rule.includeNoLabor && segmentData.job.title.toLowerCase().trim() === DEFAULT_POOL_UNKNOWN_JOB_TITLE) ||
              (rule.contributor && rule.contributor.name.toLowerCase().trim() === segmentData.job.title.toLowerCase().trim()) ||
              rule.additionalContributors?.find((additionalJob) => additionalJob.name.toLowerCase().trim() === segmentData.job.title.toLowerCase().trim())
          )
        ) {
          results.push({
            id: data.employee?.id || data.provider.employeeId, // use the kickfin employee id if present
            name: data.employee?.name || data.provider.name,
            job: { title: segmentData.job.title.toLowerCase() },
            totals: segmentData.totals,
            tickets: segmentData.tickets,
            schedules: matchingJobSchedules(segmentData.job.title, data.provider.schedules),
          });
        }
      });
  });

  return results;
};

// Validates the tip rules vs the labor data
export const validateTipRulesAgainstLaborData = (integrationData: IPOSResults, calcRules: ICalculatorRules, tipCalcRequests: ITipCalculatorRequest[]): void => {
  const integrationDataJobTitles: { title: string; total: number; employees: { name: string; total: number }[] }[] = []; // contains array of unique job titles for employees with earned tips returned from integration data.

  const integrationDataJobTitlesWithNoScheduleEnd: { title: string; total: number; employees: { name: string; total: number }[] }[] = []; // contains array of unique job titles for employees with no schedule end.
  const integrationDataEmployeesNoBreakEnd: { title: string; total: number; employees: { name: string; total: number }[] }[] = []; // contains array of employee names that have breaks without an end.
  const integrationDataInvalidTimeClockEntries: { name: string; jobTitle: string; start: string; end: string; totalMinWorked: number }[] = []; // contains array of employees with time clock entries

  const calcRulesJobTitles: string[] = []; // contains array of unique jobs titles from the tip rules.
  const calcRulesJobTitlesWithZeroPoints: string[] = []; // contains array of unique jobs titles from the tip rules with zero points;

  const calcTipShareRulesJobTitles: string[] = []; // contains array of unique jobs titles with a share policy from the tip rules.
  const calcTipShareRulesTippableJobTitles: string[] = []; // contains array of unique jobs titles with a share policy or benefitting from a share policy from the tip rules.
  const errors: ITipCalcValidationError[] = []; // Array of errors returned.

  // validate the data array contains integration data otherwise return an error
  if (integrationData.data.length === 0) {
    throw new TipCalculationNoDataError('No data found for the selected date.');
  }

  // Build job titles arrays from integration data
  integrationData.data.forEach((data) => {
    if (data.provider.schedules.length > 0) {
      data.provider.schedules.forEach((sched) => {
        // Add employees where schedule clock out is before clock in
        if (sched.start && sched.end && dayjs(sched.end).isSameOrBefore(dayjs(sched.start))) {
          integrationDataInvalidTimeClockEntries.push({ name: data.provider.name, jobTitle: sched.jobTitle, start: sched.start, end: sched.end, totalMinWorked: sched.totalMinWorked });
        }

        // Add job titles where there is no schedule end
        if (!sched.end) {
          const job = integrationDataJobTitlesWithNoScheduleEnd.find((item) => item.title.toLowerCase() === sched.jobTitle.toLowerCase());
          if (job) {
            job.employees.push({ name: data.provider.name, total: 0 });
          } else {
            integrationDataJobTitlesWithNoScheduleEnd.push({ title: sched.jobTitle.toLowerCase(), total: 0, employees: [{ name: data.provider.name, total: 0 }] });
          }
        }

        // Find employees who have breaks without an end.
        if (sched.breaks.some((item) => !item.end)) {
          const job = integrationDataEmployeesNoBreakEnd.find((item) => item.title.toLowerCase() === sched.jobTitle.toLowerCase());
          if (job) {
            job.employees.push({ name: data.provider.name, total: 0 });
          } else {
            integrationDataEmployeesNoBreakEnd.push({ title: sched.jobTitle.toLowerCase(), total: 0, employees: [{ name: data.provider.name, total: 0 }] });
          }
        }
      });
    }

    // Load job titles based on segment
    data.segments.forEach((segment) => {
      // build  jobs array
      const job = integrationDataJobTitles.find((item) => item.title.toLowerCase() === segment.job.title.toLowerCase());
      if (job) {
        job.total += segment.totals.net;
        const employee = job.employees.find((employee) => employee.name === data.provider.name);
        if (!employee) {
          job.employees.push({ name: data.provider.name, total: segment.totals.net });
        } else {
          employee.total += segment.totals.net;
        }
      } else {
        integrationDataJobTitles.push({ title: segment.job.title.toLowerCase(), total: segment.totals.net, employees: [{ name: data.provider.name, total: segment.totals.net }] });
      }
    });
  });

  // Build job titles from calculation rules
  calcRules.rules.forEach((calcRule) => {
    if (calcRule.contributor) {
      if (!calcRulesJobTitles.includes(calcRule.contributor.name.toLowerCase())) calcRulesJobTitles.push(calcRule.contributor.name.toLowerCase());
      if (!calcRule.sharePolicy && calcRule.value === 0 && !calcRulesJobTitlesWithZeroPoints.includes(calcRule.contributor.name.toLowerCase())) calcRulesJobTitlesWithZeroPoints.push(calcRule.contributor.name.toLowerCase());
    }

    // build tip share rules (if a share policy exists)
    if (calcRule.sharePolicy) {
      if (calcRule.contributor) {
        if (!calcTipShareRulesJobTitles.includes(calcRule.contributor.name.toLowerCase())) calcTipShareRulesJobTitles.push(calcRule.contributor.name.toLowerCase());
        if (!calcTipShareRulesTippableJobTitles.includes(calcRule.contributor.name.toLowerCase())) calcTipShareRulesTippableJobTitles.push(calcRule.contributor.name.toLowerCase());
      }

      // add `unknown` to handle no labor rules
      if (calcRule.includeNoLabor) {
        calcRulesJobTitles.push(DEFAULT_POOL_UNKNOWN_JOB_TITLE);
        calcTipShareRulesJobTitles.push(DEFAULT_POOL_UNKNOWN_JOB_TITLE);
        calcTipShareRulesTippableJobTitles.push(DEFAULT_POOL_UNKNOWN_JOB_TITLE);
      }

      // add additional jobs
      calcRule.additionalContributors?.forEach((additionalJob) => {
        const additionalJobTitle = additionalJob.name.toLowerCase();
        if (!calcRulesJobTitles.includes(additionalJobTitle)) calcRulesJobTitles.push(additionalJobTitle);
        if (!calcTipShareRulesJobTitles.includes(additionalJobTitle)) calcTipShareRulesJobTitles.push(additionalJobTitle);
        if (!calcTipShareRulesTippableJobTitles.includes(additionalJobTitle)) calcTipShareRulesTippableJobTitles.push(additionalJobTitle);
      });

      calcRule.sharePolicy.shares?.forEach((share) => {
        share.beneficiaries?.forEach((job) => {
          if (!calcTipShareRulesTippableJobTitles.includes(job.name.toLowerCase())) calcTipShareRulesTippableJobTitles.push(job.name.toLowerCase());
        });
      });
    }
  });

  // Check if any employee has invalid clockins
  const invalidTimeClockEntries = integrationDataInvalidTimeClockEntries.filter((item) => calcRulesJobTitles.includes(item.jobTitle.toLowerCase()));
  if (invalidTimeClockEntries?.length > 0) {
    errors.push({
      type: TipCalculationValidationType.EMPLOYEES_INVALID_TIME_CLOCK_ENTRIES,
      message: 'Please correct the time clock entries for the following employees.  Employees must clock at least one minute of time when working a tipped job.',
      employees: invalidTimeClockEntries,
    });
    throw new TipCalculationValidationError('tip calculation validation error', errors);
  }

  // Check if any employee does not have an schedule->end on any provider schedules
  const noScheduleEndJobsForTipShare = integrationDataJobTitlesWithNoScheduleEnd.filter((item) => calcTipShareRulesTippableJobTitles.includes(item.title));
  if (noScheduleEndJobsForTipShare?.length > 0) {
    errors.push({
      type: TipCalculationValidationType.EMPLOYEES_STILL_CLOCKED_IN,
      message: 'Before performing tip calculations, all tippable employees must be clocked out. Please ensure the following employees have clocked and try again.',
      jobs: noScheduleEndJobsForTipShare,
    });
    throw new TipCalculationValidationError('tip calculation validation error', errors);
  }

  // Check if any employee has a schedule without a break end.
  const noBreakEndJobsForTipShare = integrationDataEmployeesNoBreakEnd.filter((item) => calcTipShareRulesTippableJobTitles.includes(item.title));
  if (noBreakEndJobsForTipShare?.length > 0) {
    errors.push({
      type: TipCalculationValidationType.EMPLOYEES_NO_BREAK_END,
      message: 'Before performing tip calculations, all tippable employees must be clocked in from any breaks taken. Please ensure the following employees have completed their breaks.',
      jobs: noBreakEndJobsForTipShare,
    });
    throw new TipCalculationValidationError('tip calculation validation error', errors);
  }

  // Validate rule for share rules - There is no tip share rule for an employee who earned tips
  const noTipShareRuleForJob = integrationDataJobTitles.filter((element) => element.total > 0 && !calcTipShareRulesJobTitles.includes(element.title) && !calcRulesJobTitlesWithZeroPoints.includes(element.title));
  if (noTipShareRuleForJob.length > 0) {
    errors.push({
      type: TipCalculationValidationType.NO_TIP_SHARE_RULE_FOR_JOB,
      message: 'No tip rules are defined for the jobs listed below. These employees may have clocked in with the wrong job code. Either correct the job code in your point of sale, or add a tip rule for these jobs.',
      jobs: noTipShareRuleForJob,
    });

    throw new TipCalculationValidationError('tip calculation validation error', errors);
  }

  // Check if there are employees to be tipped
  if (!tipCalcRequests.filter((req) => req.employees.length > 0).length) {
    errors.push({ type: TipCalculationValidationType.NO_ELIGIBLE_EMPLOYEES, message: 'Based on your rules, there are no employees who worked matching your rules. Please check your configuration.' });
  }

  // Validate rule zero point rules - There is a tip rule without a share that has zero points
  const zeroPointsRuleForJob = integrationDataJobTitles.filter((element) => element.total > 0 && calcRulesJobTitlesWithZeroPoints.includes(element.title));
  if (zeroPointsRuleForJob.length > 0) {
    errors.push({
      type: TipCalculationValidationType.ZERO_POINT_RULE_FOR_JOB,
      message: 'The jobs below are not configured to accept tips. These employees may have clocked in with the wrong job code. If you proceed, these tips will be ignored and not disbursed by the platform.',
      jobs: zeroPointsRuleForJob,
    });
  }

  // Validate rule for job -  there is a tip calculation rule for all labor data job titles
  const noRuleForJob = integrationDataJobTitles.filter((element) => !calcRulesJobTitles.includes(element.title));
  if (noRuleForJob.length > 0) errors.push({ type: TipCalculationValidationType.NO_RULE_FOR_JOB, message: "These employees have logged hours but won't get tips based on your current rules.", jobs: noRuleForJob });

  if (errors.length) throw new TipCalculationValidationError('tip calculation validation error', errors);
};

// Match provider jobs to schedule
const matchingJobSchedules = (job: string, schedules: IPOSProviderSchedule[]): ITipCalculatorRequestEmployeeSchedule[] => {
  const matchingSchedules = schedules.filter((sched) => sched.jobTitle.toLowerCase() === job.toLowerCase());
  return matchingSchedules.map((sched) => ({ start: sched.start, end: sched.end }));
};

// Build the tip calculator requests.
// This method returns an array of requests for the tip calulator for each segment.
export const buildTipCalculatorRequests = (posData: IPOSResults, calcRules: ICalculatorRules, segments: IDaySegment[]): ITipCalculatorRequest[] => {
  // Get list of unique segments from POS results
  const uniqueSegments = getSortedUniqueSegments(posData, segments);

  let includeParticipants = false;
  // When participants are defined in the rules, check if any rule or split check is benefitting any of the types defined.
  // In this case, we send the participants along with the request to calculate the participant totals.
  if (calcRules.participants?.length > 0) {
    const participantTypes = calcRules.participants.map((participant) => participant.type);
    includeParticipants = calcRules.rules.some((rule) => rule.sharePolicy?.shares?.some((share) => share.beneficiaries.some((beneficiary) => participantTypes.includes(beneficiary.type))));

    if (!includeParticipants) {
      includeParticipants = posData.data.some((data) =>
        data.segments.some((segment) =>
          segment.tickets.some((ticket) => {
            if (ticket.override?.type === TicketOverrideType.SPLIT) {
              return ticket.override.shares.some((share) => participantTypes.includes(share.participant.type));
            }
            return false;
          })
        )
      );
    }
  }
  // Build tip calculator requests for segments
  const data = uniqueSegments?.map((segment) => ({
    segmentId: segment.id,
    segment: segment.name,
    start: segment.start || '--:--',
    end: segment.end || '--:--',
    rules: calcRules.rules,
    employees: buildTipCalculatorEmployees(posData, calcRules.rules, segment),
    categoryGroups: calcRules.categoryGroups,
    ...(includeParticipants && { participants: calcRules.participants }),
  }));

  // Check if the total tips for the day is greater than 0.
  // If the amount equals 0, we remove the processing of the segment, unless that segment also includes split checks
  const results: ITipCalculatorRequest[] = [];
  data.forEach((item) => {
    const totalNet = sumBy(item.employees, 'totals.net'); // sum total net earned for the day
    const hasSplitChecks = item.employees?.some((emp) => emp.tickets?.some((ticket) => ticket.override?.type === TicketOverrideType.SPLIT));

    if (totalNet > 0 || (totalNet === 0 && hasSplitChecks)) results.push(item);
  });

  return results;
};

// Build TipCalculatorPayees from POS data
export const buildTipCalculatorPayeeFromPOSData = (tipCalcResults: ITipCalculatorResult[], posResults: IPOSResults, siteHoldbackSetting: IHoldbackFixed | IHoldbackPercent, sharedPOSJobTitles: string[]): ITipCalculatorPayee[] => {
  const payees: ITipCalculatorPayee[] = [];

  // Assemble the payee by iterating through the labor/ticket data.
  posResults.data.forEach((result) => {
    const { employee, provider, segments } = result;

    const sharedPOS = !employee && provider.schedules.length > 0 && sharedPOSJobTitles.includes(provider.schedules[0].jobTitle.toLowerCase()); // Determine if the employee is a shared pos.

    // Get the calculated results for each employee
    // if we have the employee we use the employee->id to match
    // otherwise we use the provider employeeId.
    const calculatedResults = buildCalculatedResultsForEmployee(employee?.id || provider.employeeId, tipCalcResults);
    if (calculatedResults.length > 0 && !sharedPOS) {
      // Calculate split check totals
      const splitCheckTotal = calculatedResults.reduce((sum, calculation) => sum + (calculation.splitChecks?.received ?? []).reduce((sum, check) => sum + check.totals.total, 0), 0);
      const splitCheckAutoGratsTotal = calculatedResults.reduce((sum, calculation) => sum + (calculation.splitChecks?.received ?? []).reduce((sum, check) => sum + check.totals.autoGratuities, 0), 0);

      // Calculate payee totals
      const gross = sumBy(calculatedResults, 'calculated.total') + splitCheckTotal; // sum gross earned for the day
      const autoGratuity = sumBy(calculatedResults, 'calculated.autoGratuities') + splitCheckAutoGratsTotal; // sum autoGratuties earned for the day
      const holdbackAmount = calculateHoldbackValue(employee?.disbursementMethod || DisbursementMethod.INSTANT, siteHoldbackSetting, gross); // Calculate holdbacks
      const net = gross - holdbackAmount;

      // Assemble the payee record
      const payee: ITipCalculatorPayee = {
        id: employee?.id || provider.employeeId,
        name: employee?.name || provider.name,
        calculations: calculatedResults,
        gross,
        net,
        autoGratuity,
        holdbackAmount,
        segments,
        provider,
        employee,
      };

      if (payee.gross > 0) payees.push(payee);
    }
  });

  return payees;
};

/**
 * Builds a list of participants grouped by name and type from the given tip calculation results.
 *
 * @param {ITipCalculatorResult[]} tipCalcResults - An array of tip calculation results, where each result contains:
 *  - `segment`: The segment (e.g., "morning", "night").
 *  - `segmentId`: The ID associated with the segment.
 *  - `participants`: An array of participant objects to process.
 * @returns {ITipCalculatorParticipant[]} An array of participants, grouped by name and type, with aggregated calculations.
 */
export const buildTipCalculatorParticipants = (tipCalcResults: ITipCalculatorResult[]): ITipCalculatorParticipant[] => {
  const participants: ITipCalculatorParticipant[] = [];

  tipCalcResults.forEach((result) => {
    result.participants.forEach((participant) => {
      const existingParticipant = participants.find((p) => p.name.toLowerCase().trim() === participant.name.toLowerCase().trim() && p.type === participant.type);
      let gross = participant.calculated.total;
      let autoGratuity = participant.calculated.autoGratuities;

      // Add split check totals
      participant.splitChecks?.received.forEach((received) => {
        gross += received.totals.total;
        autoGratuity += received.totals.autoGratuities;
      });

      if (existingParticipant) {
        existingParticipant.gross += gross;
        existingParticipant.net += gross;
        existingParticipant.autoGratuity += autoGratuity;
        existingParticipant.calculations.push({ ...participant, name: result.segment, id: result.segmentId });
      } else {
        participants.push({
          name: participant.name,
          type: participant.type,
          calculations: [{ ...participant, name: result.segment, id: result.segmentId }],
          gross,
          net: gross,
          autoGratuity,
        });
      }
    });
  });

  if (!participants.length) return undefined;

  return participants;
};

// Build the calculated results for each employee
export const buildCalculatedResultsForEmployee = (id: string, tipCalcResults: ITipCalculatorResult[]): ITipCalculatorResultEmployee[] => {
  const calculatedResults: ITipCalculatorResultEmployee[] = [];

  // Iterate through the tip calculation results
  // to get each employees calculated tips
  tipCalcResults.forEach((result) => {
    const empCalculatedResult = (result?.employees || []).filter((emp) => emp.id === id);

    empCalculatedResult?.forEach((calculation) => {
      calculatedResults.push({ ...calculation, segmentId: result.segmentId });
    });
  });

  return calculatedResults;
};

// Sum the totals for each day segment
export const calculateCalculatorPaymentSetTotals = (paymentSet: ICalculatorPaymentSet, posResults: IPOSResults): ICalculatorPaymentSet => {
  let cash = 0;
  let credit = 0;

  const calculatorSegments = paymentSet.segments.map((segment) => ({ ...segment, cash: 0, credit: 0 }));

  posResults.data.forEach((data) => {
    data.segments.forEach((segmentData) => {
      const segmentIndex = calculatorSegments.findIndex((segment) => segment.id === segmentData.id);
      if (segmentIndex !== -1) {
        calculatorSegments[segmentIndex].cash += segmentData.totals.tip.cash;
        calculatorSegments[segmentIndex].credit += segmentData.totals.tip.creditCard;
      }
    });
    cash += data.totals.tip.cash;
    credit += data.totals.tip.creditCard;
  });

  return { ...paymentSet, cash, credit, segments: calculatorSegments };
};

export const isDatePayable = (date: Dayjs, offset: number, timezone: string, daySegments?: IDaySegment[], currentTime?: Dayjs): boolean => {
  const now = currentTime ? dayjs(currentTime).tz(timezone) : dayjs().tz(timezone);

  // check if is end of last segment
  if (daySegments && daySegments.length > 0) {
    const selectedDayName = date.format('dddd').toLocaleUpperCase() as Days;

    // get valid day segments
    const validDaySegments = daySegments.filter((segment) => selectedDayName && segment?.days.includes(selectedDayName));

    if (validDaySegments.length > 0) {
      const lastDaySegment = validDaySegments.sort((a: IDaySegment, b: IDaySegment) => {
        const start = parseInt(a.start.split(':').join(''), 10);
        const end = parseInt(b.start.split(':').join(''), 10);
        return start - end;
      })[validDaySegments.length - 1];

      let endLastSegment = dayjs.tz(`${date.format('YYYY-MM-DD')} ${lastDaySegment?.end}`, timezone);
      if (lastDaySegment?.endsNextDay) endLastSegment = endLastSegment.add(1, 'day');

      return now >= endLastSegment;
    }
  }

  const morningOffset = 21600; // 6:00 am - assume close is after midnight (next day)
  let businessDayEnd = dayjs.tz(date, timezone).startOf('day').add(offset, 'seconds');
  if (offset <= morningOffset) businessDayEnd = businessDayEnd.add(1, 'day');

  return now >= businessDayEnd;
};

/**
 * constructs the tip calculator's payment's calculations object.
 */
export const buildPaymentSetCalculations = (method: TipCalculatorMethod, tipCalcResults: ITipCalculatorResult[], creditCardPercentFee?: number): IPaymentSetCalculations => ({
  method: method as TipCalculatorMethod.SHARE,
  results: tipCalcResults.map((result) => ({
    ...result,
    segment: result.segmentId === DEFAULT_POOL_ID ? DEFAULT_POOL_NAME : result?.segment,
    creditCardPercentFee,
  })),
});

export const getSharedPOSJobTitles = (calcRules: ICalculatorRules): string[] => {
  const sharedPOSJobTitles: string[] = [];
  calcRules.rules.forEach((rule) => {
    if (rule.sharePolicy?.isSharedTerminal && !rule.default) sharedPOSJobTitles.push(rule.contributor.name.trim().toLowerCase());
  });

  return sharedPOSJobTitles;
};

/**
 * Checks if a given payee is the last remaining employee eligible to receive tips from a shared terminal or default pool.
 * This is useful to determine if a payee can be removed from a payment, particularly in scenarios
 * where removing them would leave no one to receive the tips collected at a shared terminal.
 *
 * @param {string} payeeId - The ID of the payee to check.
 * @param {ICalculatorRules} rules - The calculator rules containing share policies.
 * @param {IPOSResults} integrationData - The Point of Sale (POS) data, including employee and segment information.
 * @param {ICalculatorPaymentSegment[]} [segments] - Array of payment segments to consider.
 * @returns {boolean} Returns `true` if the payee is NOT the last remaining eligible employee (meaning they can be removed), and `false` if they ARE the last one (meaning they cannot be removed without leaving tips unassigned).
 */
export const lastRemainingPayeeInSharePolicy = (payeeId: string, rules: ICalculatorRules, integrationData: IPOSResults, segments: ICalculatorPaymentSegment[]): boolean => {
  // Check if we have shared terminal rules
  const sharePolicyRules = rules.rules.filter((rule) => rule.sharePolicy?.isSharedTerminal);
  if (sharePolicyRules.length === 0) return false;

  // Check if payee is the last remaining employee receiving the remainder of at least one shared pos
  return sharePolicyRules.some((shareRule) => {
    const sharePolicyJobs = shareRule.contributor ? [shareRule.contributor.name.trim().toLowerCase(), ...shareRule.additionalContributors.map((contributor) => contributor.name.trim().toLowerCase())] : [];
    if (shareRule.includeNoLabor) sharePolicyJobs.push(DEFAULT_POOL_UNKNOWN_JOB_TITLE);

    // Check if we have data for the shared policy rule job.
    const sharedPOS = integrationData.data.find((item) => !item.employee && item.provider.schedules.length > 0 && sharePolicyJobs.includes(item.provider.schedules[0].jobTitle.trim().toLowerCase()));
    if (!sharedPOS) return false; // no shared pos found matching job title

    // Build an array of segments that should be checked
    const validSegments: ICalculatorPaymentSegment[] = [];
    segments.forEach((segment) => {
      const sharedPOSSegment = sharedPOS.segments.find((s) => s.id === segment.id);
      if (sharedPOSSegment && sharedPOSSegment.totals.tip.net > 0) validSegments.push(segment);
    });

    // Check for valid segments that have a total greater than 0.
    // if none exist, we don't have to check employees who worked the shift.
    if (validSegments.length === 0) return false;

    const remainderJobs = shareRule.sharePolicy.remainder.beneficiaries.flatMap((b) => b.participants.map((p) => p.name.trim().toLowerCase()));

    // Loop through each segment and verify if payee id is the last payee working a job benefiting from the shared pos remainder
    return validSegments.some((segment) => {
      const employees = integrationData.data.filter((item) => item.segments.some((s) => s.id === segment.id && remainderJobs.includes(s.job.title.trim().toLowerCase())));
      if (employees.length === 1) {
        const employeeIdToCheck = employees[0].employee?.id || employees[0].provider?.employeeId;
        return employeeIdToCheck === payeeId; // True if payee is the only one
      }
      return false;
    });
  });
};

export const calculateCreditCardFee = (creditCardAmount: number, feePercent: number): number => Math.floor(creditCardAmount * ((feePercent || 0) / 100));

export const getSortedUniqueSegments = (integrationData: IPOSResults, segments: IDaySegment[]): IPOSResultSegment[] => {
  const uniquePOSResultSegments: IPOSResultSegment[] = [];
  const posSegments: IPOSResultSegment[] = [...integrationData.data.flatMap((item) => item.segments.map((segment) => ({ id: segment.id, name: segment.name })))];

  const seenIds = new Set<string>();
  const filteredUniqueSegments = posSegments.filter((segment) => segment && !seenIds.has(segment?.id) && seenIds.add(segment?.id));

  filteredUniqueSegments.forEach((uniqueSegment) => {
    const segment = segments.find((s) => s.id === uniqueSegment.id);
    if (segment) uniquePOSResultSegments.push(segment);
    if (!segment) uniquePOSResultSegments.push(uniqueSegment);
  });

  // sort the results by start to make sure items are listed in the correct order.
  // For `default pool, we do not have a start & end date.  We always want it to show last.
  return uniquePOSResultSegments.sort((a: IPOSResultSegment, b: IPOSResultSegment) => {
    const start = parseInt(a.start?.split(':').join(''), 10) || 10_000;
    const end = parseInt(b.start?.split(':').join(''), 10) || 10_000;
    return start - end;
  });
};

export const buildSortedChecksList = (posResults: IPOSResults): ICheck[] => {
  const checks: ICheck[] = [];

  posResults.data.forEach((item) => {
    item.segments.forEach((segment) => {
      segment.tickets.forEach((ticket) => {
        checks.push({
          ...ticket,
          segmentName: segment.name,
          employeeId: item.employee?.id || item.provider.employeeId,
          employeeName: item.employee?.name || item.provider.name || DEFAULT_POOL_UNKNOWN_JOB_TITLE,
        });
      });
    });
  });

  return checks.sort(sortAlphaNumerically('displayId'));
};

export const buildSortedParticipantList = (posResults: IPOSResults, participants: ICalculatorParticipant[] = []): ISplitParticipant[] => {
  const employees: ISplitParticipant[] = [];

  posResults?.data.forEach((item) => {
    if (item.employee) {
      employees.push({
        id: item.employee.id,
        name: item.employee.name,
        type: CalculatorParticipantType.JOB,
      });
    } else if (item.provider?.name && item.provider.name.trim().toLowerCase() !== DEFAULT_POOL_UNKNOWN_JOB_TITLE) {
      employees.push({
        id: item.provider.employeeId,
        name: item.provider.name,
        type: CalculatorParticipantType.JOB,
      });
    }
  });

  return [...employees, ...participants.map((participant) => ({ ...participant, id: participant.name.toLowerCase().replace(' ', '_') }))].sort(sortAlphaNumerically('name'));
};

/**
 * Calculates an even distribution of a gross amount among a list of ticket split shares.
 * The `gross` is distributed ensuring all cents are allocated as evenly as possible.
 *
 * @param {IPOSTicketSplitOverrideShare[]} shares - Array of share objects
 * @param {number} gross - The total gross amount to distribute.
 * @returns {IPOSTicketSplitOverrideShare[]} Updated shares with `appliedPercent` (initial even percentage),
 * `percent` (recalculated actual percentage), and `gross` (actual distributed amount).
 */
export const calculateEvenTicketSplitPercent = (shares: IPOSTicketSplitOverrideShare[], gross: number): IPOSTicketSplitOverrideShare[] => {
  const numberOfEmployees = shares.length;
  if (numberOfEmployees === 0) return [];

  const totalPercentage = 100;
  const basePercentage = Math.floor((totalPercentage * 100) / numberOfEmployees) / 100; // Base percentage with 2 decimals
  const evenPercentages = new Array(numberOfEmployees).fill(basePercentage);
  const grossDistribution = calculateDistribution(evenPercentages, gross);

  // recalculate percents so the total adds up to 100%
  const resultPercentages = calculatePercents(grossDistribution, gross);

  return shares.map((share, index) => ({
    ...share,
    appliedPercent: evenPercentages[index],
    percent: resultPercentages[index],
    gross: grossDistribution[index],
  }));
};

/**
 * Distributes a total amount among shares based on their percentages,
 * ensuring the entire total is allocated by handling remainders.
 *
 * It first calculates an initial distribution by applying each share's percentage to the total amount
 * and flooring the result. The remainder is then allocated by prioritizing shares with larger initial
 * fractional parts, followed by a round-robin distribution of cents until the `total` is fully allocated.
 *
 * @param {number[]} shares - An array of numbers representing the percentage share for each recipient (e.g., [25, 75] for 25% and 75%).
 * @param {number} total - The total amount to be distributed.
 * @returns {number[]} An array of numbers representing the actual amount distributed to each share.
 */
export const calculateDistribution = (shares: number[], total: number): number[] => {
  // Calculate the initial distribution and remove fractional parts
  const { distribution, fractionalParts, distributedTotal, indices } = shares.reduce(
    (acc, sharePercent, i) => {
      const amount = (sharePercent / 100) * total;
      const flooredAmount = Math.floor(amount);
      const decimalPart = amount - flooredAmount;

      acc.distributedTotal += flooredAmount;
      acc.distribution.push(flooredAmount);
      acc.fractionalParts.push(decimalPart);
      acc.indices.push(i);
      return acc;
    },
    { distributedTotal: 0, distribution: [], fractionalParts: [], indices: [] }
  );

  let remainder = total - distributedTotal;

  const sortedIndices = indices.sort((a, b) => fractionalParts[b] - fractionalParts[a]);

  // Distribute the remainder evenly prioritizing shares with larger initial fractional parts
  for (let i = 0; remainder > 0; i = (i + 1) % distribution.length) {
    const index = sortedIndices[i];
    distribution[index]++;
    remainder--;
  }
  return distribution;
};

const calculatePercents = (shares: number[], total: number): number[] => {
  // Calculate the initial distribution
  const percents = shares.map((share) => Math.floor(((parseFloat(share.toString()) * 100) / total) * 100) / 100);

  // Calculate the remainder
  const percentTotal = percents.reduce((sum, amount) => sum + amount, 0);
  const remainderPercentage = 100 - percentTotal;

  // Create an array of indices and sort them based on the initial percents (smallest first).
  // This prioritizes giving the remainder percents to the ones assigned less initially due to flooring.
  const sortedIndices = percents.map((_, i) => i).sort((a, b) => percents[a] - percents[b]);

  // Distribute the remainder
  for (let i = 0; i < Math.round(remainderPercentage * 100); i++) {
    const index = sortedIndices[i % shares.length];
    percents[index] += 0.01;
  }
  return percents.map((percent) => Math.round(percent * 100) / 100);
};

/**
 * Distributes a check's tip and auto-gratuity amounts based on share percentages.
 * If `calculateGross` is true, gross distribution is also recalculated based on the percentages.
 *
 * @param {IPOSTicketSplitOverrideShare[]} shares - Share objects defining participant portions.
 * @param {ICheck} check - Check object with tip and auto-gratuity totals.
 * @param {boolean} [calculateGross=false] - If true, forces recalculation of gross amounts per share.
 * @returns {IPOSTicketSplitOverrideShare[]} Updated shares with `percent`, `gross`, `tip`, and `autoGratuity`.
 */
export const calculateTicketSplitTotal = (shares: IPOSTicketSplitOverrideShare[], check: ICheck, calculateGross?: boolean): IPOSTicketSplitOverrideShare[] => {
  if (shares.length === 0) return [];

  // Get the first share to determine the distribution type
  const firstShare = shares[0];
  const hasTips = firstShare.sources.includes(RuleFundSource.TIPS);
  const hasAutoGratuities = firstShare.sources.includes(RuleFundSource.AUTO_GRATUITIES);

  const grossTotal = check.totals.gross;
  const netTipTotal = check.totals.tip.net;
  const netAutoGratuityTotal = check.totals.autoGratuity.net;

  let { grossDistribution, appliedPercents, percents } = shares.reduce(
    (acc, share) => {
      acc.grossDistribution.push(Number(share.gross));
      acc.appliedPercents.push(Number(share.appliedPercent) || 0);
      acc.percents.push(Number(share.percent) || 0);
      return acc;
    },
    { grossDistribution: [], appliedPercents: [], percents: [] }
  );

  // Percentages might not add up to 100% because of rounding issues, but we can recalculate percentages from entered amounts to ensure they add up to 100%
  const totalPercentage = Math.round(percents.reduce((sum, percent) => sum + percent, 0) * 100) / 100;
  if (totalPercentage !== 100) {
    percents = calculatePercents(
      grossDistribution,
      grossDistribution.reduce((sum, amount) => sum + amount, 0)
    );
    appliedPercents = percents;
  }

  if (calculateGross) {
    const currentGrossTotal = hasTips && hasAutoGratuities ? grossTotal : hasTips ? check.totals.tip.gross : check.totals.autoGratuity.gross;
    grossDistribution = calculateDistribution(appliedPercents, currentGrossTotal);
  }
  const tipDistribution = grossTotal === netTipTotal ? grossDistribution : calculateDistribution(appliedPercents, hasTips ? netTipTotal : 0);
  const autoGratuityDistribution = grossTotal === netAutoGratuityTotal ? grossDistribution : calculateDistribution(appliedPercents, hasAutoGratuities ? netAutoGratuityTotal : 0);

  return shares.map((share, idx) => ({
    ...share,
    percent: percents[idx],
    gross: grossDistribution[idx],
    tip: tipDistribution[idx],
    autoGratuity: autoGratuityDistribution[idx],
  }));
};

// Return an array of tip calculator warnings including segments
export const buildTipCalculatorWarnings = (results: ITipCalculatorResult[]): ITipCalculatorResultWarning[] => {
  const warnings: ITipCalculatorResultWarning[] = [];

  results?.forEach((result) => {
    result.warnings?.forEach((warning) => {
      warnings.push({ ...warning, segment: result.segment });
    });
  });

  return warnings;
};

// Calculates the totals of checks broken down by total, cash, & credit card
export const calculateTicketTotals = (tickets: IPOSTicket[]): ITicketTotals => ({
  autoGratuity: {
    gross: tickets.reduce((acc, ticket) => acc + ticket.totals.autoGratuity.gross, 0),
    net: tickets.reduce((acc, ticket) => acc + ticket.totals.autoGratuity.net, 0),
    cash: tickets.reduce((acc, ticket) => acc + ticket.totals.autoGratuity.cash, 0),
    creditCard: tickets.reduce((acc, ticket) => acc + ticket.totals.autoGratuity.creditCard, 0),
    creditCardFee: tickets.reduce((acc, ticket) => acc + ticket.totals.autoGratuity.creditCardFee, 0),
  },
  tip: {
    gross: tickets.reduce((acc, ticket) => acc + ticket.totals.tip.gross, 0),
    net: tickets.reduce((acc, ticket) => acc + ticket.totals.tip.net, 0),
    cash: tickets.reduce((acc, ticket) => acc + ticket.totals.tip.cash, 0),
    creditCard: tickets.reduce((acc, ticket) => acc + ticket.totals.tip.creditCard, 0),
    creditCardFee: tickets.reduce((acc, ticket) => acc + ticket.totals.tip.creditCardFee, 0),
  },
  gross: tickets.reduce((acc, ticket) => acc + ticket.totals.gross, 0),
  net: tickets.reduce((acc, ticket) => acc + ticket.totals.net, 0),
});

// Returns sales items grouped by category group and sorted by amount
export const buildSalesItems = (payout: ITipsBreakdown | ITipCalculatorResultEmployee, categoryGroups: ICategoryGroup[]): ITipsBreakdownLineItem[] => {
  const [groups, categories] = partition(payout.categories || [], (category) => category.type === ShareRuleType.CATEGORY_GROUP);

  const groupItems: ITipsBreakdownLineItem[] = groups
    ?.map((group) => {
      const salesGroup = categoryGroups?.find((g) => g.name.toLowerCase() === group.name.toLowerCase());
      return {
        label: toTitleCase(group.name),
        amount: group.total,
        subItems: categories
          .filter((category) => salesGroup?.categories.map((c) => c.toLowerCase()).includes(category.name.toLowerCase()))
          .map((category) => ({
            label: toTitleCase(category.name),
            amount: category.total,
          }))
          .sort((a, b) => b.amount - a.amount),
      };
    })
    .sort((a, b) => b.amount - a.amount);

  const categoryItems: ITipsBreakdownLineItem[] = categories
    .map((group) => ({
      label: toTitleCase(group.name),
      amount: group.total,
    }))
    .sort((a, b) => b.amount - a.amount);

  return [...groupItems, ...categoryItems];
};

// Returns check sales items grouped by category group and sorted by amount
export const buildCheckSalesItems = (categories: IPOSTicketCategory[], categoryGroups: ICategoryGroup[] = []): ITipsBreakdownLineItem[] => {
  // Group categories by name (case insensitive) and sum their totals
  const groupedCategories = categories.reduce(
    (acc, category) => {
      const normalizedName = category.name.toLowerCase();
      if (!acc[normalizedName]) {
        acc[normalizedName] = {
          name: category.name,
          total: 0,
        };
      }
      acc[normalizedName].total += category.total;
      return acc;
    },
    {} as Record<string, { name: string; total: number }>
  );

  // Convert grouped categories back to array format
  const uniqueCategories = Object.values(groupedCategories).map(({ name, total }) => ({
    name,
    total,
  }));

  // Use uniqueCategories instead of categories for the rest of the function
  const groupItems: ITipsBreakdownLineItem[] = categoryGroups
    .map((salesGroup) => {
      const groupItems = uniqueCategories
        .filter((category) => salesGroup?.categories.map((c) => c.toLowerCase()).includes(category.name.toLowerCase()))
        .map((category) => ({
          label: toTitleCase(category.name),
          amount: category.total,
        }))
        .sort((a, b) => b.amount - a.amount);

      return {
        label: toTitleCase(salesGroup.name),
        amount: groupItems.reduce((sum, item) => sum + item.amount, 0),
        subItems: groupItems,
      };
    })
    .sort((a, b) => b.amount - a.amount)
    .filter((item) => item.amount > 0);

  const categoryItems: ITipsBreakdownLineItem[] = uniqueCategories
    .map((group) => ({
      label: toTitleCase(group.name),
      amount: group.total,
    }))
    .sort((a, b) => b.amount - a.amount);

  return [...groupItems, ...categoryItems];
};

/**
 * Takes array of RuleFundSource (tips, auto_gratuities) and returns 'Tips & Autograts', 'Tips' or 'Autograts'
 *
 * @param sources array of RuleFundSource
 * @returns sources string
 */
export const getTipSources = (sources: RuleFundSource[]) => toTitleCase(orderBy(sources, String, 'desc').join(' & ').replace(RuleFundSource.AUTO_GRATUITIES, 'Autograts'));

interface IOverrideBreakdownData {
  splitChecks: ITipCalculatorResultSplitChecks;
  ignoredChecks: ITipCalculatorResultIgnoredCheck[];
}

/**
 * Takes array of ITipCalculatorResultEmployee and returns an object of IOverrideBreakdownData
 *
 * @param calculations array of ITipCalculatorResultEmployee
 * @returns an object of all split check data and ignored check data for an employee
 */
export const buildEmployeeOverrideTicketBreakdown = (calculations: ITipCalculatorResultEmployee[]): IOverrideBreakdownData =>
  calculations.reduce(
    (acc, calculation) => {
      if (calculation.splitChecks) {
        acc.splitChecks.received.push(...calculation.splitChecks.received);
        acc.splitChecks.contributions.push(...calculation.splitChecks.contributions);
      }

      if (calculation.ignoredChecks) acc.ignoredChecks.push(...calculation.ignoredChecks);

      return acc;
    },
    {
      splitChecks: { received: [], contributions: [] },
      ignoredChecks: [],
    }
  );

/**
 * Takes array of ITipCalculatorResultParticipant and returns an object of ITipCalculatorResultSplitChecks
 *
 * @param calculations array of ITipCalculatorResultParticipant
 * @returns an object of all split check data a participant
 */
export const buildParticipantSplitCheckBreakdown = (calculations: ITipCalculatorResultParticipant[]): ITipCalculatorResultSplitChecks =>
  calculations.reduce(
    (acc, calculation) => {
      if (calculation.splitChecks) {
        acc.received.push(...calculation.splitChecks.received);
        acc.contributions.push(...calculation.splitChecks.contributions);
      }

      return acc;
    },
    { received: [], contributions: [] }
  );

/**
 * Builds a list of check change log entries based on modifications to a check, such as edited cash tips or ignore actions.
 *
 * @param action - The type of change (e.g., CASH_EDITED, IGNORE, IGNORE_UNDO).
 * @param changeLogEntries - The existing list of change log entries.
 * @param posData - The POS results containing segment and ticket information.
 * @param check - The check that was modified.
 * @returns A new array of change log entries with applicable modifications applied.
 */
export const buildCheckChangeLogEntries = (action: ChangeLogAction, changeLogEntries: ITipCalculatorChangeLogEntry[], posData: IPOSResults, check: ICheck): ITipCalculatorChangeLogEntry[] => {
  const isSplit = [ChangeLogAction.SPLIT, ChangeLogAction.UNDO_OVERRIDE].includes(action);
  const isIgnore = [ChangeLogAction.IGNORE, ChangeLogAction.UNDO_OVERRIDE].includes(action);
  const isCashEdited = action === ChangeLogAction.CASH_EDITED;

  if (!isSplit && !isIgnore && !isCashEdited) return [...changeLogEntries];

  // Determine which entries to remove based on action
  const updatedEntries = changeLogEntries.filter((entry) => {
    const isSameCheck = (entry.details as CheckModifiedDetails)?.id === check.ticketId;
    const isRelevantAction =
      (isCashEdited && entry.action === ChangeLogAction.CASH_EDITED) ||
      (isIgnore && [ChangeLogAction.IGNORE, ChangeLogAction.UNDO_OVERRIDE].includes(entry.action)) ||
      (isSplit && [ChangeLogAction.SPLIT, ChangeLogAction.UNDO_OVERRIDE].includes(entry.action));
    return !(entry.entity === ChangeLogEntity.CHECK && isRelevantAction && isSameCheck);
  });

  // If UNDO_OVERRIDE, just return filtered list (don't add new)
  if (action === ChangeLogAction.UNDO_OVERRIDE) return updatedEntries;

  // Find POS segment and ticket
  const posDataSegment = posData.data
    .map((item) => item.segments.find((segment) => segment.tickets.some((ticket) => ticket.ticketId === check.ticketId)))
    .find((segment) => segment?.tickets.some((ticket) => ticket.ticketId === check.ticketId));

  const posTicket = posDataSegment?.tickets.find((ticket) => ticket.ticketId === check.ticketId);
  if (!posTicket) return updatedEntries;

  // For CASH_EDITED: check if there's an existing entry with matching from amount
  let existingEntry: ITipCalculatorChangeLogEntry | undefined;
  if (isCashEdited) {
    // Find existing entry with matching from amount
    existingEntry = changeLogEntries.find((entry) => entry.entity === ChangeLogEntity.CHECK && entry.action === ChangeLogAction.CASH_EDITED && (entry.details as CheckModifiedDetails)?.id === check.ticketId);

    if ((existingEntry?.details as CheckModifiedDetails)?.from === check.totals.tip.cash) return updatedEntries;
  }

  // Base change log entry shared across IGNORE, SPLIT and CASH_EDITED
  const baseEntry: ITipCalculatorChangeLogEntry = {
    entity: ChangeLogEntity.CHECK,
    action,
    participant: {
      name: check.employeeName,
      type: ChangeLogParticipantType.JOB,
      job: posDataSegment.job.title,
    },
    segment: posDataSegment.name,
    details: {
      id: check.ticketId,
      displayId: check.displayId,
      ...(isCashEdited && {
        from: (existingEntry?.details as CheckModifiedDetails)?.from ?? posTicket.totals.tip.cash,
        to: check.totals.tip.cash,
      }),
    } as CheckModifiedDetails,
    timestamp: dayjs().utc().toISOString(),
  };

  return [...updatedEntries, baseEntry];
};

interface ILaborChangeLogEmployee {
  id: string;
  name: string;
  segmentName?: string;
  jobTitle?: string;
  from?: number;
  to?: number;
}

/**
 * Builds and manages change log entries for labor-related modifications.
 * This function handles three types of labor changes:
 * - Removal of labor data
 * - Removal of labor segments
 * - Editing of segment durations
 *
 * @param {ChangeLogAction} action - The type of action being performed (REMOVED, SEGMENT_REMOVED, SEGMENT_DURATION_EDITED)
 * @param {ITipCalculatorChangeLogEntry[]} changeLogEntries - The existing array of change log entries
 * @param {ILaborChangeLogEmployee} employee - The employee data being modified
 * @param {string} employee.id - The unique identifier of the employee
 * @param {string} employee.name - The name of the employee
 * @param {string} [employee.segmentName] - Optional name of the segment the employee belongs to
 * @param {string} [employee.jobTitle] - Optional job title of the employee
 * @param {number} [employee.from] - Optional start time for segment duration edits
 * @param {number} [employee.to] - Optional end time for segment duration edits
 * @returns {ITipCalculatorChangeLogEntry[]} Updated array of change log entries
 */
export const buildLaborChangeLogEntries = (action: ChangeLogAction, changeLogEntries: ITipCalculatorChangeLogEntry[], employee: ILaborChangeLogEmployee): ITipCalculatorChangeLogEntry[] => {
  const isRemoved = [ChangeLogAction.REMOVED].includes(action);
  const isRemovedSegment = [ChangeLogAction.SEGMENT_REMOVED].includes(action);
  const isSegmentDurationEdited = [ChangeLogAction.SEGMENT_DURATION_EDITED].includes(action);

  if (!isRemoved && !isRemovedSegment && !isSegmentDurationEdited) return [...changeLogEntries];

  // Determine which entries to remove based on action
  const updatedEntries = changeLogEntries.filter((entry) => {
    const isSameEmployee = (entry.details as LaborDataDetails)?.id === employee.id;
    const isRelevantAction =
      (isSegmentDurationEdited &&
        entry.action === ChangeLogAction.SEGMENT_DURATION_EDITED &&
        entry.segment === employee.segmentName &&
        entry.participant.type === ChangeLogParticipantType.JOB &&
        entry.participant.job === employee.jobTitle) ||
      (isRemoved && [ChangeLogAction.REMOVED].includes(entry.action)) ||
      (isRemovedSegment && [ChangeLogAction.SEGMENT_REMOVED].includes(entry.action));
    return !(entry.entity === ChangeLogEntity.LABOR_DATA && isRelevantAction && isSameEmployee);
  });

  // For SEGMENT_DURATION_EDITED: check if there's an existing entry with matching from value
  let existingEntry: ITipCalculatorChangeLogEntry | undefined;
  if (isSegmentDurationEdited) {
    existingEntry = changeLogEntries.find(
      (entry) =>
        entry.entity === ChangeLogEntity.LABOR_DATA &&
        entry.action === ChangeLogAction.SEGMENT_DURATION_EDITED &&
        entry.segment === employee.segmentName &&
        entry.participant.type === ChangeLogParticipantType.JOB &&
        entry.participant.job === employee.jobTitle &&
        (entry.details as LaborDataDetails)?.id === employee.id
    );

    if ((existingEntry?.details as LaborDataDetails)?.from === employee.to) return updatedEntries;
  }

  // Base change log entry
  const entry: ITipCalculatorChangeLogEntry = {
    entity: ChangeLogEntity.LABOR_DATA,
    action,
    participant: {
      name: employee.name,
      type: employee.jobTitle ? ChangeLogParticipantType.JOB : ChangeLogParticipantType.EMPLOYEE,
      ...(employee.jobTitle && { job: employee.jobTitle }),
    },
    details: {
      id: employee.id,
    } as LaborDataDetails,
    ...(employee.segmentName && { segment: employee.segmentName }),
    timestamp: dayjs().utc().toISOString(),
  };

  if (isSegmentDurationEdited) {
    entry.details.from = existingEntry?.details.from ?? employee.from;
    entry.details.to = employee.to;

    // Don't add entry if from & to are equal
    if (entry.details.from === entry.details.to) return updatedEntries;
  }

  return [...updatedEntries, entry];
};

interface ITipDataAmountTotals {
  id: string;
  name: string;
  segmentName: string;
  jobTitle: string;
  source: SourceType;
  type: SourcePaymentType;
  value: number;
  totals: IPOSSegmentTotals;
}

export const buildTipDataChangeLogEntries = (tipData: ITipDataAmountTotals, changeLogEntries: ITipCalculatorChangeLogEntry[]): ITipCalculatorChangeLogEntry[] => {
  // Set action
  let action: ChangeLogAction;
  switch (tipData.type) {
    case SourcePaymentType.CREDIT:
      action = tipData.source === SourceType.TIP ? ChangeLogAction.CREDIT_CARD_TIPS_EDITED : ChangeLogAction.CREDIT_AUTOGRATS_EDITED;
      break;
    case SourcePaymentType.CASH:
      action = tipData.source === SourceType.TIP ? ChangeLogAction.CASH_TIPS_EDITED : ChangeLogAction.CASH_AUTOGRATS_EDITED;
      break;
    default:
      return changeLogEntries;
  }

  const changeLogEntry = {
    entity: ChangeLogEntity.TIP_DATA,
    action,
    participant: { name: tipData.name, type: ChangeLogParticipantType.JOB, job: tipData.jobTitle },
    details: { id: tipData.id, from: tipData.totals[tipData.source][tipData.type], to: tipData.value },
    segment: tipData.segmentName,
    timestamp: dayjs().utc().toISOString(),
  };

  const existingEntry = changeLogEntries.find(
    (entry) =>
      entry.entity === ChangeLogEntity.TIP_DATA &&
      entry.action === changeLogEntry.action &&
      entry.segment === changeLogEntry.segment &&
      entry.participant.type === ChangeLogParticipantType.JOB &&
      entry.participant.job === changeLogEntry.participant.job &&
      entry.details.id === changeLogEntry.details.id
  );

  // Determine which entries to remove based on action
  const updatedEntries = changeLogEntries.filter((entry) => {
    const isSameEmployee = (entry.details as TipEditedDetails)?.id === changeLogEntry.details.id;
    const isRelevantAction = entry.action === changeLogEntry.action && entry.segment === changeLogEntry.segment && entry.participant.type === ChangeLogParticipantType.JOB && entry.participant.job === changeLogEntry.participant.job;

    return !(entry.entity === ChangeLogEntity.TIP_DATA && isRelevantAction && isSameEmployee);
  });

  // If the existing entry has the same from and to values, don't add a new entry
  if ((existingEntry?.details.from ?? changeLogEntry.details.from) === changeLogEntry.details.to) return updatedEntries;

  // Build change log entry
  return [...updatedEntries, { ...changeLogEntry, details: { ...changeLogEntry.details, from: existingEntry?.details.from ?? changeLogEntry.details.from } }];
};

export interface IChangeLogTableRow {
  number: number;
  category: string;
  event: string;
  item: { name: string; segment?: string; job?: string };
  details?: { from: string; to: string };
}

/**
 * Transforms an array of change log entries into formatted table rows for display.
 * Handles various types of changes including labor data, tip data, and check modifications.
 *
 * @param {ITipCalculatorChangeLogEntry[]} entries - Array of change log entries to be formatted
 * @returns {IChangeLogTableRow[]} Array of formatted table rows containing:
 *   - number: Sequential row number
 *   - category: Formatted entity type (Labor, Tip Data, Checks)
 *   - event: Formatted action type (Cash Tips Edited, Hours Edited, Split, etc.)
 *   - item: Object containing participant name, segment, and job information, or check number for check-related changes
 *   - details: Optional object containing formatted 'from' and 'to' values for changes that involve modifications
 */
export const buildChangeLogTableRows = (entries: ITipCalculatorChangeLogEntry[]): IChangeLogTableRow[] => {
  const formatEntity = (entity: ChangeLogEntity) => {
    switch (entity) {
      case ChangeLogEntity.LABOR_DATA:
        return 'Labor';
      case ChangeLogEntity.TIP_DATA:
        return 'Tip Data';
      case ChangeLogEntity.CHECK:
        return 'Checks';
      default:
        return entity;
    }
  };

  const formatAction = (action: ChangeLogAction) => {
    switch (action) {
      case ChangeLogAction.CASH_EDITED:
        return 'Cash Tips Edited';
      case ChangeLogAction.IGNORE:
        return 'Ignored';
      case ChangeLogAction.SPLIT:
        return 'Split';
      case ChangeLogAction.SEGMENT_DURATION_EDITED:
        return 'Hours Edited';
      case ChangeLogAction.REMOVED:
        return 'Employee Removed';
      case ChangeLogAction.SEGMENT_REMOVED:
        return 'Job Removed';
      case ChangeLogAction.CREDIT_CARD_TIPS_EDITED:
        return 'CC Tips Edited';
      case ChangeLogAction.CREDIT_AUTOGRATS_EDITED:
        return 'CC Autograts Edited';
      case ChangeLogAction.CASH_AUTOGRATS_EDITED:
        return 'Cash Autograts Edited';
      case ChangeLogAction.CASH_TIPS_EDITED:
        return 'Cash Tips Edited';
      default:
        return action;
    }
  };

  const formatDetails = (entry: ITipCalculatorChangeLogEntry) => {
    switch (entry.action) {
      case ChangeLogAction.CASH_EDITED:
      case ChangeLogAction.CREDIT_CARD_TIPS_EDITED:
      case ChangeLogAction.CREDIT_AUTOGRATS_EDITED:
      case ChangeLogAction.CASH_AUTOGRATS_EDITED:
      case ChangeLogAction.CASH_TIPS_EDITED:
        return { from: centsToDollarsDisplay(entry.details.from), to: centsToDollarsDisplay(entry.details.to) };
      case ChangeLogAction.SEGMENT_DURATION_EDITED:
        return { from: new Duration(entry.details.from).toString(), to: new Duration(entry.details.to).toString() };
      case ChangeLogAction.SPLIT:
      case ChangeLogAction.IGNORE:
        return undefined;
      default:
        return undefined;
    }
  };

  const formatItem = (entry: ITipCalculatorChangeLogEntry) => {
    if (entry.entity === ChangeLogEntity.CHECK) return { name: `#${(entry.details as CheckModifiedDetails).displayId}` };
    return { name: entry.participant?.name || '', segment: entry.segment, job: entry.participant?.job };
  };

  return entries.map((entry, index) => ({
    number: index + 1,
    category: formatEntity(entry.entity),
    event: formatAction(entry.action),
    item: formatItem(entry),
    details: formatDetails(entry),
  }));
};
